/**
 * Reads EXIF orientation from image file and returns corrected canvas with proper rotation.
 * Handles smartphone photo orientation issues.
 * @param {File} file - Image file to process
 * @returns {Promise<Blob>} - Corrected image blob
 */
export const getCorrectedImageBlob = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.onload = (e) => {
      const arrayBuffer = e.target.result;
      const exifOrientation = getExifOrientation(arrayBuffer);
      
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load image'));
      };

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        try {
          const correctedCanvas = rotateCanvasImage(img, exifOrientation);
          correctedCanvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Failed to generate blob from canvas'));
              }
            },
            file.type || 'image/jpeg',
            0.95
          );
        } catch (error) {
          reject(error);
        }
      };

      img.src = objectUrl;
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Extracts EXIF orientation value from image data
 * @param {ArrayBuffer} arrayBuffer - Image file as ArrayBuffer
 * @returns {number} - EXIF orientation value (1-8)
 */
const getExifOrientation = (arrayBuffer) => {
  const view = new DataView(arrayBuffer);

  if (view.getUint16(0, false) !== 0xffd8) {
    // Not a JPEG
    return 1;
  }

  const length = view.byteLength;
  let offset = 2;

  while (offset < length) {
    const marker = view.getUint16(offset, false);
    offset += 2;

    if (marker === 0xffe1) {
      const little = view.getUint8(offset + 10) === 0x49;
      offset += 12;

      const tags = view.getUint16(offset, little);
      offset += 2;

      for (let i = 0; i < tags; i++) {
        const tag = view.getUint16(offset, little);
        offset += 2;

        if (tag === 0x0112) {
          // Orientation tag
          return view.getUint16(offset + 6, little);
        }

        offset += 10;
      }
    } else if ((marker & 0xff00) === 0xff00) {
      offset += view.getUint16(offset, false);
    } else {
      break;
    }
  }

  return 1;
};

/**
 * Rotates canvas image based on EXIF orientation
 * @param {HTMLImageElement} img - Image element
 * @param {number} orientation - EXIF orientation value
 * @returns {HTMLCanvasElement} - Rotated canvas
 */
const rotateCanvasImage = (img, orientation) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  let width = img.width;
  let height = img.height;

  // Set canvas dimensions based on orientation
  if (orientation > 4 && orientation < 9) {
    canvas.width = height;
    canvas.height = width;
  } else {
    canvas.width = width;
    canvas.height = height;
  }

  // Apply rotation transformations
  switch (orientation) {
    case 2:
      ctx.transform(-1, 0, 0, 1, width, 0);
      break;
    case 3:
      ctx.transform(-1, 0, 0, -1, width, height);
      break;
    case 4:
      ctx.transform(1, 0, 0, -1, 0, height);
      break;
    case 5:
      ctx.transform(0, 1, 1, 0, 0, 0);
      break;
    case 6:
      ctx.transform(0, 1, -1, 0, height, 0);
      break;
    case 7:
      ctx.transform(0, -1, -1, 0, height, width);
      break;
    case 8:
      ctx.transform(0, -1, 1, 0, 0, width);
      break;
    case 1:
    default:
      break;
  }

  ctx.drawImage(img, 0, 0);
  return canvas;
};
